import React, {useState, useCallback, useMemo, useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Check, X} from '@phosphor-icons/react';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import logoDashbo from '../../images/logo_dashbo_white.svg';
import {Step1ClientData} from './steps/Step1ClientData';
import {persistOnboardingPlatformDraft, Step2Platforms} from './steps/Step2Platforms';
import {Step3ControlLines} from './steps/Step3ControlLines';
import {Step4CampaignOnboarding} from './steps/Step4CampaignOnboarding';
import './ClientOnboardingWizard.scss';

const STEPS = [
    {key: 'client_data', labelKey: 'onboarding_wizard.step1_label', shortLabelKey: 'onboarding_wizard.step1_short', skippable: false},
    {key: 'control_lines', labelKey: 'onboarding_wizard.step2_label', shortLabelKey: 'onboarding_wizard.step2_short', skippable: false},
    {key: 'platforms', labelKey: 'onboarding_wizard.step3_label', shortLabelKey: 'onboarding_wizard.step3_short', skippable: true},
    {key: 'campaigns', labelKey: 'onboarding_wizard.step4_label', shortLabelKey: 'onboarding_wizard.step4_short', skippable: false},
];

export function ClientOnboardingWizard() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialClientId = searchParams.get('client_id') || searchParams.get('clientId') || null;
    const initialClientName = searchParams.get('client_name') || searchParams.get('clientName') || '';
    const initialStep = initialClientId && searchParams.get('step') === '2' ? 1 : 0;
    const returnToMarketing = searchParams.get('source') === 'marketing';

    const [activeStep, setActiveStep] = useState(initialStep);
    const [clientId, setClientId] = useState(initialClientId);
    const [clientName, setClientName] = useState(initialClientName);
    const [clientDraft, setClientDraft] = useState({
        name: initialClientName,
        businessType: '',
        industry: '',
    });
    const [businessUnitDraft, setBusinessUnitDraft] = useState(null);
    const [platformDraft, setPlatformDraft] = useState(null);
    const [businessUnitId, setBusinessUnitId] = useState(null);
    const [materializingDraft, setMaterializingDraft] = useState(false);
    const [materializeError, setMaterializeError] = useState(null);

    useEffect(() => {
        document.body.classList.add('client-onboarding-lock');
        return () => { document.body.classList.remove('client-onboarding-lock'); };
    }, []);

    const goNext = useCallback(() => {
        setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
    }, []);

    const goBack = useCallback(() => {
        if (initialStep > 0 && activeStep <= initialStep) {
            navigate(`/client/${clientId}/control-central`);
            return;
        }
        setActiveStep((s) => Math.max(s - 1, 0));
    }, [activeStep, clientId, initialStep, navigate]);

    const handleStep1Complete = useCallback((newClientId, newClientName, draft) => {
        setClientId(newClientId);
        setClientName(newClientName);
        setClientDraft(draft);
        goNext();
    }, [goNext]);

    const handleStep3Complete = useCallback((draft) => {
        setBusinessUnitDraft(draft);
        goNext();
    }, [goNext]);

    const createOrUpdateClientFromDraft = useCallback(async () => {
        if (clientId) return clientId;

        const createResponse = await new APIClient(API_URL + 'client/add').call('POST', {name: clientDraft.name.trim()});
        if (!createResponse || createResponse.status !== 'OK') {
            throw new Error(createResponse?.error_message || t('onboarding_wizard.generic_error'));
        }

        const nextClientId = createResponse.data;

        try {
            await new APIClient(API_URL + 'client/' + nextClientId + '/profile').call('PUT', {
                business_type: clientDraft.businessType || null,
                industry: clientDraft.industry || null,
                primary_kpi: null,
                notes: null,
            });
        } catch (profileErr) { /* non-blocking */ }

        setClientId(nextClientId);
        return nextClientId;
    }, [clientDraft, clientId, t]);

    const createBusinessUnitFromDraft = useCallback(async (nextClientId) => {
        if (businessUnitId) return businessUnitId;
        if (!businessUnitDraft) throw new Error(t('onboarding_wizard.generic_error'));

        const payload = {
            ...businessUnitDraft,
            client_id: nextClientId,
        };
        delete payload.raw_budget_partial_goals;

        const response = await new APIClient(API_URL + 'business-unit/add').call('POST', payload);
        if (!response || response.status !== 'OK') {
            throw new Error(response?.error_message || t('onboarding_wizard.generic_error'));
        }

        const nextBusinessUnitId = response.data.business_unit_id;
        setBusinessUnitId(nextBusinessUnitId);
        return nextBusinessUnitId;
    }, [businessUnitDraft, businessUnitId, t]);

    const materializeDraftAndGoNext = useCallback(async (draft) => {
        setPlatformDraft(draft);
        setMaterializingDraft(true);
        setMaterializeError(null);

        try {
            const nextClientId = await createOrUpdateClientFromDraft();
            const nextBusinessUnitId = await createBusinessUnitFromDraft(nextClientId);
            await persistOnboardingPlatformDraft({
                clientId: nextClientId,
                businessUnitId: nextBusinessUnitId,
                selectedItems: draft.selectedItems,
                t,
            });
            setClientId(nextClientId);
            setBusinessUnitId(nextBusinessUnitId);
            goNext();
        } catch (error) {
            setMaterializeError(error.message || t('onboarding_wizard.generic_error'));
        } finally {
            setMaterializingDraft(false);
        }
    }, [createBusinessUnitFromDraft, createOrUpdateClientFromDraft, goNext, t]);

    const handleAbort = useCallback(() => {
        const confirmed = window.confirm(t('onboarding_wizard.exit_confirm'));
        if (!confirmed) return;
        if (clientId) {
            navigate(`/client/${clientId}/control-central`);
        } else if (returnToMarketing) {
            navigate('/marketing');
        } else {
            navigate('/agency/clients');
        }
    }, [clientId, navigate, returnToMarketing, t]);

    const handleFinish = useCallback(() => {
        if (!clientId) {
            navigate('/agency/clients');
            return;
        }
        navigate(`/client/${clientId}/control-central`);
    }, [clientId, navigate]);

    const handleSkip = useCallback(() => {
        if (activeStep === STEPS.length - 1) {
            handleFinish();
        } else {
            goNext();
        }
    }, [activeStep, goNext, handleFinish]);

    const stepInfos = useMemo(() => STEPS.map((step, index) => ({
        ...step,
        completed: index < activeStep,
        active: index === activeStep,
        index,
    })), [activeStep]);

    return (
        <div className="client-onboarding">
            <header className="client-onboarding-header">
                <div className="client-onboarding-header-left">
                    <img src={logoDashbo} alt="Dashbo" className="client-onboarding-logo"/>
                </div>
                <div className="client-onboarding-header-center">
                    <ol className="client-onboarding-stepper">
                        {stepInfos.map((step) => (
                            <li
                                key={step.key}
                                className={`client-onboarding-stepper-item${step.completed ? ' -completed' : ''}${step.active ? ' -active' : ''}`}
                            >
                                <span className="client-onboarding-stepper-dot">
                                    {step.completed
                                        ? <Check size={12} weight="bold"/>
                                        : <span>{step.index + 1}</span>}
                                </span>
                                <span className="client-onboarding-stepper-label">
                                    {t(step.shortLabelKey)}
                                </span>
                            </li>
                        ))}
                    </ol>
                </div>
                <div className="client-onboarding-header-right">
                    <button
                        type="button"
                        className="client-onboarding-close"
                        onClick={handleAbort}
                        title={t('onboarding_wizard.exit')}
                        aria-label={t('onboarding_wizard.exit')}
                    >
                        <X size={18} weight="bold"/>
                    </button>
                </div>
            </header>

            <main className="client-onboarding-main">
                <div className={`client-onboarding-panel${activeStep > 0 ? ' -wide' : ''}`}>
                    {activeStep === 0 && (
                        <Step1ClientData
                            clientId={clientId}
                            initialName={clientName}
                            initialBusinessType={clientDraft.businessType}
                            initialIndustry={clientDraft.industry}
                            initialMode={clientDraft.mode || (returnToMarketing ? 'existing' : undefined)}
                            onComplete={handleStep1Complete}
                        />
                    )}
                    {activeStep === 1 && (
                        <Step3ControlLines
                            clientId={clientId}
                            clientName={clientName}
                            initialValues={businessUnitDraft}
                            onComplete={handleStep3Complete}
                            onBack={goBack}
                            stepNumber={2}
                        />
                    )}
                    {activeStep === 2 && (
                        <Step2Platforms
                            clientId={clientId}
                            businessUnitId={businessUnitId}
                            draftMode={true}
                            initialSelectedAccountKeys={platformDraft?.selectedAccountKeys}
                            onDraftComplete={materializeDraftAndGoNext}
                            onSkip={handleSkip}
                            onBack={goBack}
                            stepNumber={3}
                        />
                    )}
                    {activeStep === 2 && materializeError && <p className="onboarding-error">{materializeError}</p>}
                    {activeStep === 2 && materializingDraft && (
                        <div className="onboarding-blocking-loader">
                            <span className="onboarding-btn-spinner -dark" aria-hidden="true"/>
                            <span>{t('onboarding_wizard.preparing_campaign_setup')}</span>
                        </div>
                    )}
                    {activeStep === 3 && (
                        <Step4CampaignOnboarding
                            businessUnitId={businessUnitId}
                            onFinish={handleFinish}
                            onBack={goBack}
                        />
                    )}
                </div>
            </main>
        </div>
    );
}

export default ClientOnboardingWizard;
